import React from 'react';
import {motion} from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import CTAButton from '../components/CTAButton';
import TrustSignals from '../components/TrustSignals';
import * as FiIcons from 'react-icons/fi';

const {FiHome, FiShield, FiAlertTriangle, FiCheck, FiDollarSign, FiBriefcase, FiTool, FiDroplet, FiWind} = FiIcons;

const CommercialPropertyPage = () => {
  const threats = [
    {
      title: "Theft of business goods and gear",
      description: "You turn your back and someone steals your business laptop from your store. You'll need a replacement.",
      icon: FiShield
    },
    {
      title: "Inventory damage", 
      description: "A pipe bursts and ruins all inventory in your stockroom. Your business is literally underwater.",
      icon: FiDroplet
    },
    {
      title: "Building structural damage",
      description: "A tree unexpectedly falls on your shop's roof. Replacement could be costly.",
      icon: FiHome
    },
    {
      title: "Damage to business fixtures",
      description: "Furniture, flooring and custom-built shelving get singed in a fire. Rebuilding might be expensive.",
      icon: FiTool
    },
    {
      title: "Equipment failures",
      description: "Your HVAC unit stops working the day after the warranty expires.",
      icon: FiAlertTriangle
    },
    {
      title: "Business income interruptions",
      description: "A storm damages your business and you have to close for three weeks for repairs.",
      icon: FiDollarSign
    }
  ];

  const riskTypes = [
    {name: "Theft and burglary", icon: FiShield},
    {name: "Vandalism", icon: FiAlertTriangle},
    {name: "Fire", icon: FiAlertTriangle},
    {name: "Water", icon: FiDroplet},
    {name: "Wind", icon: FiWind},
    {name: "Hail", icon: FiWind}
  ];

  const industries = [
    {
      name: "Construction",
      description: "Protect tools, equipment, and materials from theft and damage on job sites."
    },
    {
      name: "Food & Beverage",
      description: "Coverage for kitchen equipment, inventory, and restaurant fixtures."
    },
    {
      name: "Retail & E-Commerce",
      description: "Protection for inventory, POS systems, and store fixtures."
    },
    {
      name: "Cleaning Services",
      description: "Coverage for cleaning equipment, supplies, and business vehicles."
    },
    {
      name: "Professional Services",
      description: "Protect office equipment, furniture, and business technology."
    }
  ];

  const businessTypes = [
    {
      name: "Restaurants and food service",
      description: "Kitchen equipment, inventory, and dining area fixtures protection."
    },
    {
      name: "Retail and e-commerce",
      description: "Inventory, POS systems, and merchandise display coverage."
    },
    {
      name: "Sports and fitness",
      description: "Exercise equipment, locker rooms, and facility fixtures protection."
    },
    {
      name: "Arts and entertainment",
      description: "Specialized equipment, instruments, and performance venue coverage."
    },
    {
      name: "Beauty and personal care",
      description: "Salon equipment, product inventory, and facility fixtures protection."
    }
  ];

  const coverageAreas = [
    "Theft of business gear from your store",
    "Inventory damage",
    "Building structural damage", 
    "Damage to business fixtures you own",
    "Business income interruption",
    "Equipment failure"
  ];

  const costFactors = [
    "Your industry",
    "Your business operations",
    "The size of your business",
    "Where you do business",
    "Your business needs and how much coverage you choose",
    "Policy limits"
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-600 to-primary-800 text-white py-20">
        <div className="absolute inset-0 bg-black opacity-60"></div>
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-40"
          style={{backgroundImage: 'url(https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80)'}}
        ></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <motion.h1
              initial={{opacity: 0, y: 30}}
              animate={{opacity: 1, y: 0}}
              transition={{duration: 0.8}}
              className="text-4xl md:text-6xl font-bold mb-6"
            >
              Commercial Property Insurance
            </motion.h1>
            <motion.p
              initial={{opacity: 0, y: 30}}
              animate={{opacity: 1, y: 0}}
              transition={{duration: 0.8, delay: 0.2}}
              className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto"
            >
              Protect your gear, goods and buildings with customized coverage. Starting at just $18/month!
            </motion.p>
            <motion.div
              initial={{opacity: 0, y: 30}}
              animate={{opacity: 1, y: 0}}
              transition={{duration: 0.8, delay: 0.4}}
            >
              <CTAButton 
                text="Get Your Commercial Property Quote" 
                size="xl" 
                className="mb-8"
              />
            </motion.div>
            <motion.div
              initial={{opacity: 0}}
              animate={{opacity: 1}}
              transition={{duration: 0.8, delay: 0.6}}
            >
              <TrustSignals className="text-white" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Industry Selection */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Choose Your Primary Industry to Get Started
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            <motion.div
              initial={{opacity: 0, y: 30}}
              animate={{opacity: 1, y: 0}}
              transition={{duration: 0.6}}
              className="text-center p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border border-blue-200 hover:shadow-xl transition-all duration-300"
            >
              <div className="bg-blue-500 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiBriefcase} className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">Construction</h3>
              <CTAButton text="Get a Quote" size="sm" className="w-full" />
            </motion.div>

            <motion.div
              initial={{opacity: 0, y: 30}}
              animate={{opacity: 1, y: 0}}
              transition={{duration: 0.6, delay: 0.1}}
              className="text-center p-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg border border-green-200 hover:shadow-xl transition-all duration-300"
            >
              <div className="bg-green-500 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiHome} className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">Food & Beverage</h3>
              <CTAButton text="Get a Quote" size="sm" className="w-full" />
            </motion.div>

            <motion.div
              initial={{opacity: 0, y: 30}}
              animate={{opacity: 1, y: 0}}
              transition={{duration: 0.6, delay: 0.2}}
              className="text-center p-6 bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg border border-purple-200 hover:shadow-xl transition-all duration-300"
            >
              <div className="bg-purple-500 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiShield} className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">Retail & E-Commerce</h3>
              <CTAButton text="Get a Quote" size="sm" className="w-full" />
            </motion.div>

            <motion.div
              initial={{opacity: 0, y: 30}}
              animate={{opacity: 1, y: 0}}
              transition={{duration: 0.6, delay: 0.3}}
              className="text-center p-6 bg-gradient-to-br from-yellow-50 to-orange-50 rounded-lg border border-yellow-200 hover:shadow-xl transition-all duration-300"
            >
              <div className="bg-yellow-500 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiTool} className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">Cleaning</h3>
              <CTAButton text="Get a Quote" size="sm" className="w-full" />
            </motion.div>

            <motion.div
              initial={{opacity: 0, y: 30}}
              animate={{opacity: 1, y: 0}}
              transition={{duration: 0.6, delay: 0.4}}
              className="text-center p-6 bg-gradient-to-br from-gray-50 to-slate-50 rounded-lg border border-gray-200 hover:shadow-xl transition-all duration-300"
            >
              <div className="bg-gray-500 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiAlertTriangle} className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">Other Industries</h3>
              <CTAButton text="Get a Quote" size="sm" className="w-full" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* What is Commercial Property Insurance */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{opacity: 0, x: -30}}
              animate={{opacity: 1, x: 0}}
              transition={{duration: 0.8}}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                What is Commercial Property Insurance?
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                Commercial property insurance can help owners and renters protect the physical assets of a business, such as inventory, fixtures, furniture, building structures and other business property.
              </p>
              <p className="text-lg text-gray-700 mb-8">
                Whether you own or rent your business space, commercial property insurance provides essential protection for the physical assets that keep your business running.
              </p>
              <div className="bg-green-50 p-6 rounded-lg border-l-4 border-green-400">
                <h3 className="text-xl font-bold text-green-800 mb-3">Starting at $18/month†</h3>
                <p className="text-gray-700">Buy online in about 10 minutes, and cancel anytime.</p>
              </div>
            </motion.div>
            <motion.div
              initial={{opacity: 0, x: 30}}
              animate={{opacity: 1, x: 0}}
              transition={{duration: 0.8}}
              className="bg-primary-50 p-8 rounded-lg"
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">What Can Commercial Property Insurance Cover?</h3>
              <div className="space-y-4">
                {coverageAreas.map((area, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <SafeIcon icon={FiCheck} className="h-5 w-5 text-primary-600 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">{area}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Risk Types Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              What Type of Risks Can Harm Your Business?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Commercial property insurance protects against various risks that could damage your business assets.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {riskTypes.map((risk, index) => (
              <motion.div
                key={risk.name}
                initial={{opacity: 0, y: 30}}
                animate={{opacity: 1, y: 0}}
                transition={{duration: 0.6, delay: index * 0.1}}
                className="text-center p-6 bg-red-50 rounded-lg border border-red-200 hover:shadow-lg transition-shadow"
              >
                <div className="bg-red-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={risk.icon} className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="text-sm font-bold text-gray-900">{risk.name}</h3>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Threats Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Insurance for Commercial Property Can Help Protect the Goods You Need for Business
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {threats.map((threat, index) => (
              <motion.div
                key={threat.title}
                initial={{opacity: 0, y: 30}}
                animate={{opacity: 1, y: 0}}
                transition={{duration: 0.6, delay: index * 0.1}}
                className="bg-white p-8 rounded-lg shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                  <SafeIcon icon={threat.icon} className="h-8 w-8 text-primary-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{threat.title}</h3>
                <p className="text-gray-600">{threat.description}</p>
              </motion.div>
            ))}
          </div>
          <motion.div
            initial={{opacity: 0, y: 30}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.6, delay: 0.8}}
            className="text-center mt-12"
          >
            <CTAButton text="Get Instant Quote" />
          </motion.div>
        </div>
      </section>

      {/* Cost Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              How Much Does Commercial Property Insurance Cost?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Commercial property insurance policies can cost as little as $18/month† for some low-risk businesses.
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{opacity: 0, x: -30}}
              animate={{opacity: 1, x: 0}}
              transition={{duration: 0.8}}
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                Several factors influence the price you'll pay for insurance, including:
              </h3>
              <ul className="space-y-3">
                {costFactors.map((factor, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <SafeIcon icon={FiCheck} className="h-5 w-5 text-secondary-600 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{factor}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
            <motion.div
              initial={{opacity: 0, x: 30}}
              animate={{opacity: 1, x: 0}}
              transition={{duration: 0.8}}
              className="bg-primary-50 p-8 rounded-lg"
            >
              <div className="text-center">
                <div className="bg-primary-600 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <SafeIcon icon={FiDollarSign} className="h-10 w-10 text-white" />
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-4">Starting at $18/month†</h3>
                <p className="text-gray-700 mb-6">
                  Get your exact price with a free online quote in about 10 minutes.
                </p>
                <CTAButton text="Get Instant Quote" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How to Get Quote */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              How to Get a Commercial Property Insurance Online Quote
            </h2>
          </div>
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{opacity: 0, y: 30}}
              animate={{opacity: 1, y: 0}}
              transition={{duration: 0.6}}
              className="bg-gradient-to-br from-primary-50 to-blue-50 p-8 rounded-lg border border-primary-200"
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="text-center">
                  <div className="bg-primary-600 text-white rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                    1
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">Start a free online quote</h3>
                  <p className="text-gray-600 text-sm">In about 10 minutes you'll see the exact price you'll pay for commercial property insurance online.</p>
                </div>
                <div className="text-center">
                  <div className="bg-primary-600 text-white rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                    2
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">Customize your coverage</h3>
                  <p className="text-gray-600 text-sm">Adjust your policy limits and deductibles to pick the right coverage at the right price.</p>
                </div>
                <div className="text-center">
                  <div className="bg-primary-600 text-white rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                    3
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">Buy 100% online</h3>
                  <p className="text-gray-600 text-sm">Your coverage is effective immediately after payment. And your certificate of insurance is ready at no extra cost.</p>
                </div>
              </div>
              <div className="text-center">
                <CTAButton text="Get Instant Quote" size="lg" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Who Needs Commercial Property Insurance */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Who Needs Commercial Rental Property and Commercial Property Owner Insurance?
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {businessTypes.map((business, index) => (
              <motion.div
                key={business.name}
                initial={{opacity: 0, y: 30}}
                animate={{opacity: 1, y: 0}}
                transition={{duration: 0.6, delay: index * 0.1}}
                className="bg-gray-50 p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow text-center"
              >
                <div className="bg-primary-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={FiHome} className="h-6 w-6 text-primary-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">{business.name}</h3>
                <p className="text-gray-600 text-sm">{business.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary-600 to-primary-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h2
            initial={{opacity: 0, y: 30}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.6}}
            className="text-3xl md:text-4xl font-bold mb-6"
          >
            Protect Your Business Property Today
          </motion.h2>
          <motion.p
            initial={{opacity: 0, y: 30}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.6, delay: 0.2}}
            className="text-xl mb-8"
          >
            Don't let theft, damage, or disasters shut down your business. Get comprehensive commercial property coverage starting at just $18/month.
          </motion.p>
          <motion.div
            initial={{opacity: 0, y: 30}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.6, delay: 0.4}}
          >
            <CTAButton 
              text="Get Your Commercial Property Quote Now" 
              size="xl" 
              className="bg-white text-primary-600 hover:bg-gray-100"
            />
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CommercialPropertyPage;